import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

// Desktop-only: whether the user has manually collapsed the sidebar to give
// the active feature more width. Entirely separate from SideNavigatorService,
// which drives the mobile off-canvas show/hide behavior - collapsing here
// must never affect what happens on mobile/tablet.
const STORAGE_KEY = "sidebarCollapsed";

function readPersisted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class SidebarCollapseService {
  private collapsedSource = new BehaviorSubject<boolean>(readPersisted());
  collapsed$ = this.collapsedSource.asObservable();

  get collapsed(): boolean {
    return this.collapsedSource.value;
  }

  toggle(): void {
    this.set(!this.collapsedSource.value);
  }

  collapse(): void {
    this.set(true);
  }

  expand(): void {
    this.set(false);
  }

  private set(value: boolean): void {
    this.collapsedSource.next(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      // Storage unavailable - collapsed state just won't persist across reloads
    }

    // The sidebar's width change is CSS-driven (padding-left on
    // app-react-native-bundle/#app-home), so no native resize event ever
    // fires even though the mounted feature's actual content box just
    // changed size - confirmed empirically: a real browser window resize to
    // an equivalent width lays out perfectly (react-native-web features
    // recompute correctly), while this CSS-only change leaves them stuck
    // with layout/scroll math computed at mount time, breaking scrolling.
    //
    // react-native-web's Dimensions module (which useWindowDimensions is
    // built on) listens for 'resize' on window.visualViewport specifically
    // when it exists (virtually all current browsers), NOT on window itself
    // - dispatching only on window silently misses that listener entirely.
    // Firing on both covers real-browser behavior either way.
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
      window.visualViewport?.dispatchEvent(new Event("resize"));
    });
  }
}
